/**
 * useImportToast
 *
 * The success toast for contacts added to the address book by an import. An
 * import works without an account: the contacts are kept on the device. Only
 * the server backup needs a verified phone, so for an unverified session the
 * toast also says the import isn't backed up yet and offers to verify, standing
 * in for the separate backup reminder the change would otherwise raise.
 */

import { useCallback } from 'react';
import { useIonRouter } from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from '@/modules/idv';
import { BackupReminder, PHONE_STEP_PATH } from './backupReminder';

export function useImportToast(): (message: string) => void {
  const [presentToast] = useAppToast();
  const { claims } = useUserContext();
  const router = useIonRouter();
  const phoneVerified = ClaimsLogic.isPhoneVerified(claims);

  return useCallback(
    (message: string) => {
      if (phoneVerified) {
        presentToast({ message, duration: 2000, color: 'success' });
        return;
      }
      BackupReminder.markShown();
      presentToast({
        message: `${message}. Verify your phone to back up your address book.`,
        duration: 8000,
        color: 'success',
        buttons: [
          {
            text: 'Verify',
            handler: () => {
              router.push(PHONE_STEP_PATH, 'forward');
            },
          },
        ],
      });
    },
    [phoneVerified, presentToast, router]
  );
}
